import { View } from 'react-native';
import { useEffect, useState } from 'react';
import useLoader from '@/utils/hooks/useLoader';
import { DateTime } from 'luxon';
import { supabase } from '@/data/client';
import { exportHTMLToPDFWithLoader } from '@/utils/exportPDF';
import { Query } from '@/utils/react-query/Queries';
import { isRoleVisibleToUser } from '@/data/roles';

const PersonProfilePDF = ({ profile, customFieldValues, personCustomFieldSections, trainingProgress, onClose }) => {
    const loader = useLoader();
    const [avatarBase64, setAvatarBase64] = useState(null);
    const { data: loggedInUser } = Query.people.loggedInUser();

    // Get signed URL for avatar
    useEffect(() => {
        const getAvatarSignedUrl = async () => {
            if (!profile?.avatarStorageKey) return;

            try {
                const { data, error } = await supabase.storage
                    .from(process.env.EXPO_PUBLIC_STORAGE_BUCKET)
                    .createSignedUrl(profile.avatarStorageKey, 60); // 60 seconds expiry

                const response = await fetch(data.signedUrl);
                const blob = await response.blob();

                if (!error && data?.signedUrl) {
                    const base64 = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result.split(',')[1]);
                        reader.readAsDataURL(blob);
                    });
                    
                    setAvatarBase64(base64);
                }
            } catch (error) {
                console.error('Error getting avatar signed URL:', error);
            }
        };

        getAvatarSignedUrl();
    }, [profile?.avatarStorageKey]);

    const formatCustomFieldValue = (value, type) => {
        if (!value) return "";
        switch (type) {
            case "Text":
            case "Long Text":
            case "Number":
            case "Dropdown":
                return value;
            case "Multiselect":
                return Array.isArray(value) ? value.join(", ") : value;
            case "Date": {
                let localFormat = DateTime.fromFormat(value, "MM/dd/yyyy");
                if (!localFormat.isValid) localFormat = DateTime.fromSQL(value);
                if (!localFormat.isValid) localFormat = DateTime.fromISO(value);
                return localFormat.toLocaleString(DateTime.DATE_MED);
            }
            case "Checkbox":
                return value ? "Yes" : "No";
            case "File Upload":
                return value.name;
            default:
                return value;
        }
    };

    const getAgeString = (dateOfBirth) => {
        const today = DateTime.now();
        const birthDate = DateTime.fromISO(dateOfBirth);
        let age = today.year - birthDate.year;
        const m = today.month - birthDate.month;
        if (m < 0 || (m === 0 && today.day < birthDate.day)) age--;
        if (age === 0) {
            let months = m < 0 ? m + 12 : m;
            if (today.day < birthDate.day) months--;
            return months === 1 ? '1 month' : `${months} months`;
        }
        return age === 1 ? '1 year' : `${age} years`;
    };

    const formatDate = (date) => {
        if (!date) return '-';
        // Handle both Date objects and ISO strings
        if (date instanceof Date) {
            return DateTime.fromJSDate(date).toLocaleString(DateTime.DATE_MED);
        }
        return DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED);
    };

    const generateHTML = () => {
        const dob = profile?.dateOfBirth ? DateTime.fromISO(profile.dateOfBirth) : null;

        const family = (profile?.Family?.FamilyMembers || []).filter(fm => fm.id !== profile?.id);
        
        const customFieldSections = personCustomFieldSections?.map(s => {
            const fields = s.PersonCustomFields?.filter(item => customFieldValues?.[item.id]);
            if (!fields?.length) return null;
            return { name: s.name, fields };
        }).filter(Boolean) || [];

        const roles = profile?.Roles?.filter(r => isRoleVisibleToUser(r, loggedInUser)) || [];
        const smallGroups = profile?.SmallGroupsAsMember || [];
        const training = trainingProgress || [];

        return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="color-scheme" content="light">
    <title>${profile?.firstName} ${profile?.lastName} - Profile</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        html, body {
            color-scheme: light;
        }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            font-size: 12px;
            line-height: 1.5;
            color: #333 !important;
            background: #f5f5f5 !important;
            padding: 20px;
        }
        .container {
            max-width: 800px;
            margin: 0 auto;
            background: #ffffff !important;
            padding: 40px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .logo-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #6366f1;
        }
        .logo {
            font-size: 24px;
            font-weight: bold;
            color: #6366f1;
            letter-spacing: 2px;
        }
        .header {
            display: flex;
            align-items: center;
            gap: 20px;
            margin-bottom: 30px;
        }
        .avatar {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            object-fit: cover;
            flex-shrink: 0;
        }
        .avatar-placeholder {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: linear-gradient(135deg, #6366f1, #8b5cf6);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 28px;
            font-weight: bold;
            flex-shrink: 0;
        }
        .header-info {
            flex: 1;
        }
        .name {
            font-size: 28px;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 8px;
        }
        .meta {
            font-size: 14px;
            color: #6b7280;
        }
        .section {
            margin-bottom: 30px;
        }
        .section-title {
            font-size: 16px;
            font-weight: bold;
            color: #6366f1;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 15px;
            padding-bottom: 8px;
            border-bottom: 1px solid #e5e7eb;
        }
        .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 20px 40px;
        }
        .info-item {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #f3f4f6;
        }
        .info-label {
            font-weight: bold;
            color: #6b7280;
        }
        .info-value {
            font-weight: normal;
            color: #1f2937;
            text-align: right;
        }
        .stats-row {
            display: flex;
            gap: 20px;
            margin: 20px 0;
            padding: 15px;
            background: #f9fafb;
            border-radius: 6px;
        }
        .stat-item {
            flex: 1;
            text-align: center;
        }
        .stat-label {
            font-size: 10px;
            text-transform: uppercase;
            color: #6b7280;
            margin-bottom: 4px;
        }
        .stat-value {
            font-size: 18px;
            font-weight: bold;
            color: #1f2937;
        }
        .tag {
            display: inline-block;
            background: #e0e7ff;
            color: #4338ca;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 500;
            margin: 2px;
        }
        .family-item, .group-item, .training-item {
            padding: 10px;
            background: #f9fafb;
            border-radius: 6px;
            margin-bottom: 8px;
        }
        .family-name, .group-name, .training-name {
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 4px;
        }
        .family-role, .group-teacher, .training-meta {
            font-size: 11px;
            color: #6b7280;
        }
        .empty {
            color: #9ca3af;
            font-style: italic;
        }
        @media print {
            body {
                background: #ffffff !important;
                padding: 0;
            }
            .container {
                box-shadow: none;
                padding: 20px;
                background: #ffffff !important;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="logo-row">
            <div class="logo">REACH</div>
            <div style="font-size: 11px; color: #9ca3af;">Person Profile Report</div>
        </div>
        <div class="header">
            ${avatarBase64 ? `<img src="data:image/png;base64,${avatarBase64}" class="avatar" alt="${profile?.firstName} ${profile?.lastName}" />` : `
            <div class="avatar-placeholder">
                ${profile?.firstName ? profile.firstName.charAt(0) : ''}${profile?.lastName ? profile.lastName.charAt(0) : ''}
            </div>`}
            <div class="header-info">
                <h1 class="name">${profile?.firstName} ${profile?.lastName}</h1>
                <div class="meta">
                    ${profile?.email ? `<div>Email: ${profile.email}</div>` : ''}
                    ${profile?.Phones?.[0]?.phoneNumber ? `<div>Phone: ${profile.Phones[0].phoneNumber}</div>` : ''}
                </div>
            </div>
        </div>

        <div class="stats-row">
            <div class="stat-item">
                <div class="stat-label">Last Attended</div>
                <div class="stat-value">${formatDate(profile?.mostRecentVisit)}</div>
            </div>
            <div class="stat-item">
                <div class="stat-label">Total Visits</div>
                <div class="stat-value">${profile?.timesAttended || 0}</div>
            </div>
            <div class="stat-item">
                <div class="stat-label">First Visit</div>
                <div class="stat-value">${formatDate(profile?.firstVisit)}</div>
            </div>
        </div>

        <div class="section">
            <div class="section-title">Personal Information</div>
            <div class="info-grid">
                <div class="info-item">
                    <span class="info-label">Gender</span>
                    <span class="info-value">${profile?.gender || '-'}</span>
                </div>
                ${dob ? `
                <div class="info-item">
                    <span class="info-label">Birthday</span>
                    <span class="info-value">${dob.toLocaleString({ day: "numeric", month: "long", year: "numeric" })} (${getAgeString(profile.dateOfBirth)} old)</span>
                </div>
                ` : ''}
                ${profile?.Address ? `
                <div class="info-item" style="grid-column: span 2;">
                    <span class="info-label">Address</span>
                    <span class="info-value">${profile.Address.street}${profile.Address.streetLine2 ? `, ${profile.Address.streetLine2}` : ''}, ${profile.Address.city}, ${profile.Address.state} ${profile.Address.zip}</span>
                </div>
                ` : ''}
                ${profile?.InvitedBy ? `
                <div class="info-item" style="grid-column: span 2;">
                    <span class="info-label">Invited By</span>
                    <span class="info-value">${profile.InvitedBy.firstName} ${profile.InvitedBy.lastName}</span>
                </div>
                ` : ''}
            </div>
        </div>

        ${customFieldSections.length > 0 ? customFieldSections.map(section => `
            <div class="section">
                <div class="section-title">${section.name}</div>
                <div class="info-grid">
                    ${section.fields.map(field => `
                        <div class="info-item">
                            <span class="info-label">${field.name}</span>
                            <span class="info-value">${formatCustomFieldValue(customFieldValues[field.id], field.type)}</span>
                        </div>
                    `).join('')}
                </div>
            </div>
        `).join('') : ''}

        ${roles.length > 0 ? `
            <div class="section">
                <div class="section-title">Roles</div>
                <div>
                    ${roles.map(role => `<span class="tag">${role.name}</span>`).join('')}
                </div>
            </div>
        ` : ''}

        ${family.length > 0 ? `
            <div class="section">
                <div class="section-title">Family Members</div>
                ${family.map(fm => `
                    <div class="family-item">
                        <div class="family-name">${fm.firstName} ${fm.lastName}</div>
                        <div class="family-role">${fm.familyRole || 'Family Member'}</div>
                    </div>
                `).join('')}
            </div>
        ` : ''}

        ${smallGroups.length > 0 ? `
            <div class="section">
                <div class="section-title">Small Groups (as Member)</div>
                ${smallGroups.map(group => `
                    <div class="group-item">
                        <div class="group-name">${group.name}</div>
                        ${group.Leader ? `<div class="group-teacher">Leader: ${group.Leader.firstName} ${group.Leader.lastName}</div>` : ''}
                    </div>
                `).join('')}
            </div>
        ` : ''}

        ${training.length > 0 ? `
            <div class="section">
                <div class="section-title">Training Enrollments</div>
                ${training.map(t => `
                    <div class="training-item">
                        <div class="training-name">${t.moduleName}</div>
                        <div class="training-meta">
                            Status: ${t.enrollmentStatus} | 
                            Progress: ${t.completionPercent}% | 
                            ${t.enrolledAt ? `Enrolled: ${formatDate(t.enrolledAt)}` : ''}
                        </div>
                    </div>
                `).join('')}
            </div>
        ` : ''}

        <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #9ca3af; text-align: center;">
            Generated on ${DateTime.now().toLocaleString(DateTime.DATETIME_MED)} by Reach CHMS
        </div>
    </div>
</body>
</html>
        `;
    };

    const handleExportPDF = async () => {
        const html = generateHTML();
        await exportHTMLToPDFWithLoader({
            html,
            filename: `${profile?.firstName}_${profile?.lastName}_Profile`,
            title: `Share ${profile?.firstName}'s Profile`,
            orientation: 'portrait',
        }, loader);
        onClose?.();
    };

    useEffect(() => {
        // Wait for avatar to load before exporting
        if (avatarBase64 !== null || !profile?.avatarStorageKey) {
            handleExportPDF();
        }
    }, [avatarBase64]);

    return (
        <View style={{ display: 'none' }}>
            {/* This component doesn't render anything visible */}
        </View>
    );
};

export default PersonProfilePDF;
